import type { ChangeEvent } from 'react';

import {
  CopyToClipboard,
  Heading,
  InputDescription,
  SaveButton,
  SelectionInputLayout,
  TextInput,
  Toggle,
  classNames,
} from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetLsproDefaultSettingsQuery,
  useGetLsproSecuritySettingsQuery,
  useUpdateLiveStreamProSecuritySettingsMutation,
  useUpdateLsproDefaultSettingsMutation,
} from '../../../../generated/graphql-manager';
import { useCurrentUser } from '../../../hooks/current-user';
import { useGetFeatures } from '../../../hooks/use-get-features';

import managerStyles from '../../../manager.module.css';
import styles from '../channel-settings.module.css';

export function SecurityPage() {
  const { lsproId } = useCurrentUser();
  const { t } = useTranslation();
  const { isWatermarkingFeatureEnabled } = useGetFeatures();

  const [sharedSecretVmpro, setSharedSecretVmpro] = useState('');
  const [sharedSecretVmproError, setSharedSecretVmproError] = useState('');

  const { data: defaultSettingsData } = useGetLsproDefaultSettingsQuery({ variables: { lsproId } });
  const defaultSettings = defaultSettingsData?.lsproSettings?.defaultSettings;

  const [updateLsproDefaultSettings] = useUpdateLsproDefaultSettingsMutation();

  const { data } = useGetLsproSecuritySettingsQuery({
    variables: { lsproId },
    onCompleted: (data) => {
      setSharedSecretVmpro(data?.lsproSettings?.securitySettings?.sharedSecretVmpro || '');
    },
  });

  const securityToken = data?.lsproSettings?.securitySettings?.tokenGenerationSecret;

  const [updateLiveStreamProSecuritySettings, { loading: updateLsproSecuritySettingsLoading }] =
    useUpdateLiveStreamProSecuritySettingsMutation();

  const validateSharedSecret = (value: string): string => {
    if (!value) return '';

    if (value.length !== 32) return t('manager.channelSettings.security.secretVmproValidation.length');

    const hexPattern = /^[0-9a-fA-F]+$/;
    if (!hexPattern.test(value)) return t('manager.channelSettings.security.secretVmproValidation.format');

    return '';
  };

  const handleSaveVmproSecret = async () => {
    const error = validateSharedSecret(sharedSecretVmpro);
    setSharedSecretVmproError(error);

    if (error) return;

    await updateLiveStreamProSecuritySettings({
      variables: {
        input: {
          lsproId,
          sharedSecretVmpro: sharedSecretVmpro,
        },
      },
      optimisticResponse: {
        updateLiveStreamProSecuritySettings: {
          __typename: 'UpdateLiveStreamProSecuritySettingsSuccess',
          sharedSecretVmpro: sharedSecretVmpro,
        },
      },
      onCompleted: (dataCompleted) => {
        if (
          dataCompleted.updateLiveStreamProSecuritySettings.__typename === 'UpdateLiveStreamProSecuritySettingsFailure'
        ) {
          setSharedSecretVmpro(data?.lsproSettings?.securitySettings.sharedSecretVmpro || '');
        }
      },
    });
  };

  const handleSharedSecretChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSharedSecretVmpro(event.target.value);
    setSharedSecretVmproError('');
  };

  const handleWatermarkingChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    updateLsproDefaultSettings({
      variables: {
        input: {
          lsproId,
          [name]: checked,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateLiveStreamProDefaultSettings: {
          __typename: 'UpdateLiveStreamProDefaultSettingsSuccess',
          defaultSettings: {
            __typename: 'LiveStreamProDefaultSettings',
            ...defaultSettings,
            allowTranscodingProfileSelection: defaultSettings?.allowTranscodingProfileSelection ?? false,
            privateEventAsDefault: defaultSettings?.privateEventAsDefault ?? false,
            restrictAttendanceReport: defaultSettings?.restrictAttendanceReport ?? false,
            singleSignOnAsDefault: defaultSettings?.singleSignOnAsDefault ?? false,
            showWatermarkingAsDefault: defaultSettings?.showWatermarkingAsDefault ?? false,
            defaultIngestSettings: defaultSettings?.defaultIngestSettings ?? {
              __typename: 'DefaultRtmpIngestSettings',
              authenticationEnabled: false,
            },
            [name]: checked,
          },
        },
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'LiveStreamProSettings', lsproId }),
          fields: {
            defaultSettings: (existingDefaultSettings = {}) => ({
              ...existingDefaultSettings,
              [name]: checked,
            }),
          },
        });
      },
    });
  };

  return (
    <main className={classNames(managerStyles.main, styles.wrapper)} data-testid="security-page">
      <Heading className={managerStyles.grayText} data-testid="security-heading">
        {t('manager.channelSettings.security.heading')}
      </Heading>

      <InputDescription inputId="security-token" label={t('manager.channelSettings.security.tokenLabel')}>
        <TextInput readOnly value={securityToken || ''} data-testid="security-token">
          <CopyToClipboard value={securityToken || ''} notificationPosition="left" />
        </TextInput>
      </InputDescription>

      <InputDescription
        inputId="shared-secret-vmpro"
        label={t('manager.channelSettings.security.secretVmproLabel')}
        helpMessage={t('manager.channelSettings.security.secretVmproHelpMessage')}
        errorMessage={sharedSecretVmproError}
      >
        <TextInput value={sharedSecretVmpro} data-testid="shared-secret-vmpro" onChange={handleSharedSecretChange}>
          <CopyToClipboard value={sharedSecretVmpro} notificationPosition="left" />
        </TextInput>
      </InputDescription>

      <div className={managerStyles.buttons}>
        <SaveButton
          data-testid="security-submit-button"
          saveLabel={t('manager.channelSettings.security.saveButton')}
          onClick={handleSaveVmproSecret}
          saving={updateLsproSecuritySettingsLoading}
          disabled={updateLsproSecuritySettingsLoading}
        />
      </div>

      {isWatermarkingFeatureEnabled && defaultSettings?.showWatermarkingAsDefault !== undefined && (
        <SelectionInputLayout
          hintMessage={t('manager.channelSettings.settings.watermarking.description')}
          checked={defaultSettings?.showWatermarkingAsDefault}
          variant="ghost"
          inputElement={
            <Toggle
              data-testid="default-settings-watermarking-toggle"
              name="showWatermarkingAsDefault"
              checked={defaultSettings?.showWatermarkingAsDefault}
              onChange={handleWatermarkingChange}
            >
              {t('manager.channelSettings.settings.watermarking.label')}
            </Toggle>
          }
        />
      )}
    </main>
  );
}
